import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";

// Global state to manage Zoho widget instances and prevent conflicts
let activeInstance: string | null = null;
const widgetCallbacks: Map<string, (element: HTMLElement | null) => void> =
  new Map();

interface ZohoWidgetProps {
  // Props to control positioning relative to other elements
  showAboveGoUpButton?: boolean;
  isMobile?: boolean;
  hasBottomNav?: boolean;
  // Add instance ID to prevent conflicts when multiple components exist
  instanceId?: string;
}

export default function ZohoWidget({
  showAboveGoUpButton = false,
  isMobile = false,
  hasBottomNav = false,
  instanceId = "default",
}: ZohoWidgetProps) {
  const [isZohoReady, setIsZohoReady] = useState(false);
  const [zohoWidget, setZohoWidget] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const isActiveInstance = useRef(false);

  // Function to claim control of the Zoho widget
  const claimWidget = useCallback(() => {
    const widget = document.getElementById("zsiq_float");
    // Give priority to the "products" instance over "global"
    const canClaim =
      widget &&
      (!activeInstance ||
        activeInstance === instanceId ||
        (instanceId === "products" && activeInstance === "global"));

    if (canClaim) {
      activeInstance = instanceId;
      isActiveInstance.current = true;
      setZohoWidget(widget);
      setIsZohoReady(true);

      // Inject CSS to ensure our positioning takes precedence and style the chat button
      if (!document.getElementById("zoho-widget-override")) {
        const style = document.createElement("style");
        style.id = "zoho-widget-override";
        style.innerHTML = `
          /* Style the SalesIQ chat button */
          #zsiq_float {
            position: fixed !important;
            right: 24px !important;
            left: auto !important;
            top: auto !important;
            transform: none !important;
            margin: 0 !important;
            z-index: 40 !important;
            
            /* Make the button smaller and circular */
            width: 48px !important;
            height: 48px !important;
            min-width: 48px !important;
            min-height: 48px !important;
            max-width: 48px !important;
            max-height: 48px !important;
            border-radius: 50% !important;
            overflow: hidden !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
          }
          
          /* Center icons and images in the chat button */
          #zsiq_float svg,
          #zsiq_float img,
          #zsiq_float i,
          #zsiq_float::before,
          #zsiq_float::after {
            margin: 0 auto !important;
            display: block !important;
            text-align: center !important;
            vertical-align: middle !important;
            max-width: 20px !important;
            max-height: 20px !important;
            width: 20px !important;
            height: 20px !important;
            transform: none !important;
          }
          
          /* Center all child elements */
          #zsiq_float > *,
          #zsiq_float div,
          #zsiq_float span,
          #zsiq_float button {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
          }
          
          /* Font sizes for the button */
          #zsiq_float {
            font-size: 14px !important;
            pointer-events: auto !important;
          }
          
          /* Ensure no invisible overlays block clicks */
          #zsiq_float * {
            pointer-events: auto !important;
          }
          
          #zsiq_float span,
          #zsiq_float div {
            font-size: 12px !important;
            text-align: center !important;
            vertical-align: middle !important;
            line-height: 1 !important;
            margin: 0 !important;
          }
        `;
        document.head.appendChild(style);
      }

      // Notify all instances about the widget state
      widgetCallbacks.forEach((callback, id) => {
        callback(id === instanceId ? widget : null);
      });

      return widget;
    }
    return null;
  }, [instanceId]);

  // Function to release control of the widget
  const releaseWidget = useCallback(() => {
    if (isActiveInstance.current) {
      activeInstance = null;
      isActiveInstance.current = false;
      setZohoWidget(null);
      setIsZohoReady(false);

      // Notify all instances
      widgetCallbacks.forEach((callback) => {
        callback(null);
      });
    }
  }, []);

  // Register this instance and try to claim widget
  useEffect(() => {
    // Register callback for this instance
    widgetCallbacks.set(instanceId, (widget) => {
      if (widget && activeInstance === instanceId) {
        setZohoWidget(widget);
        setIsZohoReady(true);
      } else {
        setZohoWidget(null);
        setIsZohoReady(false);
      }
    });

    // Try to claim the widget immediately
    const widget = claimWidget();

    if (!widget) {
      // If not found, set up polling to check periodically
      const interval = setInterval(() => {
        const foundWidget = claimWidget();
        if (foundWidget) {
          clearInterval(interval);
        }
      }, 500);

      // Cleanup interval after 10 seconds to avoid infinite polling
      const timeout = setTimeout(() => {
        clearInterval(interval);
      }, 10000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
        widgetCallbacks.delete(instanceId);
        releaseWidget();
      };
    }

    return () => {
      widgetCallbacks.delete(instanceId);
      releaseWidget();
    };
  }, [instanceId, claimWidget, releaseWidget]);

  // Update widget positioning when props change
  useEffect(() => {
    if (!zohoWidget || !isZohoReady) return;

    // Add a small delay to ensure positioning updates are applied correctly
    const positioningTimeout = setTimeout(() => {
      // Calculate bottom position based on current state
      let bottomPosition: string;

      // Adjusted positioning logic - clear separation from go-up button
      if (isMobile && hasBottomNav) {
        // Mobile with bottom navigation
        bottomPosition = showAboveGoUpButton ? "140px" : "80px";
      } else if (showAboveGoUpButton) {
        // Desktop with go-up button showing - clear gap
        bottomPosition = "80px";
      } else {
        // Default positioning
        bottomPosition = "20px";
      }

      // Remove any existing inline styles that might conflict
      zohoWidget.style.removeProperty("left");
      zohoWidget.style.removeProperty("top");
      zohoWidget.style.removeProperty("transform");

      // Apply positioning styles with important to override any existing styles
      const styles = {
        position: "fixed",
        bottom: bottomPosition,
        right: "24px",
        zIndex: "40", // Lower z-index to not interfere with mobile menu
        display: "block",
        transition: "bottom 0.3s ease-in-out",
        left: "auto", // Override any left positioning
        top: "auto", // Override any top positioning
        transform: "none", // Override any transforms
      };

      // Apply each style individually with important priority
      Object.entries(styles).forEach(([key, value]) => {
        zohoWidget.style.setProperty(key, value, "important");
      });

      // Force a style recalculation
      zohoWidget.offsetHeight;
    }, 100); // 100ms delay to ensure proper state updates

    return () => clearTimeout(positioningTimeout);
  }, [
    zohoWidget,
    isZohoReady,
    showAboveGoUpButton,
    isMobile,
    hasBottomNav,
    location.pathname,
  ]);

  // Hide widget on certain pages where it might not be appropriate
  useEffect(() => {
    if (!zohoWidget) return;

    const hiddenPaths = ["/get-started", "/login", "/register", "/signup", "/verify", "/onboarding", "/setup"];
    const shouldHide = hiddenPaths.some((path) =>
      location.pathname.startsWith(path)
    );

    if (shouldHide) {
      zohoWidget.style.display = "none";
    } else {
      zohoWidget.style.display = "block";
    }
  }, [location.pathname, zohoWidget]);

  // This component doesn't render anything visible - it just manages the existing Zoho widget
  return null;
}
